import { useCallback, useMemo, useState } from 'react'

import { TruncateWithTooltip } from '~/components/TruncateWithTooltip'
import { Button, List } from '~/components/component-library'
import { ListItemProps } from '~/components/component-library/List/types'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import {
  SecondaryDisplayButtons,
  SimulationDisplayProps,
} from '~/components/txn-display/SecondaryDisplayButtons'
import { ContractCallSection } from '~/components/txn-display/calls/ContractCallSection'
import { buildDisplayCallListItems } from '~/components/txn-display/calls/buildDisplayCallListItems'
import { DecodedCallSummary } from '~/components/txn-display/state-change/types'
import { RemoveBatchOperationHandler } from '~/components/txn-display/types'
import { TriangleAlert } from '~/icons/TriangleAlert'
import { BatchOperation, IAddress } from '~/types'

export const BatchOperationsSection = ({
  batchOperations,
  chainId,
  smartAccountAddress,
  allowRemove = false,
  removingOperationId,
  onRemoveOperation,
  decodedCalls,
  failingCallIndexes,
  tenderlyUrl,
  simulationUrl,
  runTenderlySimulation,
}: SimulationDisplayProps & {
  batchOperations: BatchOperation[]
  chainId: number
  smartAccountAddress: IAddress
  allowRemove?: boolean
  removingOperationId?: string
  onRemoveOperation?: RemoveBatchOperationHandler
  decodedCalls?: DecodedCallSummary[]
  failingCallIndexes?: number[]
}) => {
  const [showCallsByOpId, setShowCallsByOpId] = useState<
    Record<string, boolean>
  >({})
  const toggleShowCalls = useCallback(
    (operationId: string) =>
      setShowCallsByOpId((prev) => ({
        ...prev,
        [operationId]: !prev[operationId],
      })),
    [],
  )

  const items = useMemo<ListItemProps[]>(() => {
    const failingIndexSet = new Set(failingCallIndexes)
    let offset = 0
    return batchOperations.map((operation) => {
      const operationStart = offset
      const operationEnd = offset + operation.calls.length
      const operationDecodedCalls = decodedCalls?.slice(
        operationStart,
        operationEnd,
      )
      offset = operationEnd

      const hasSimulationFailure = operation.calls.some((_, i) =>
        failingIndexSet.has(operationStart + i),
      )

      const callItems = buildDisplayCallListItems({
        calls: operation.calls,
        chainId,
        smartAccountAddress,
        decodedCalls: operationDecodedCalls,
      })

      const isShowingCalls = !!showCallsByOpId[operation.operationId]
      const titleNode = (
        <TransactionTitle
          transaction={{ titleChunks: operation.titleChunks, chainId }}
          unstyled
        />
      )

      return {
        key: operation.operationId,
        content: (
          <TruncateWithTooltip content={titleNode}>
            {titleNode}
          </TruncateWithTooltip>
        ),
        meta: hasSimulationFailure ? (
          <TriangleAlert className="size-4 fill-orange-600 dark:fill-orange-400" />
        ) : undefined,
        expandableContent: (
          <ContractCallSection
            items={callItems}
            decodedCalls={operationDecodedCalls}
            isShowingCalls={isShowingCalls}
            label={callItems.length === 1 ? 'Contract call' : 'Contract calls'}
            secondaryDisplayButtons={
              <SecondaryDisplayButtons
                isShowingCalls={isShowingCalls}
                onToggleCalls={() => toggleShowCalls(operation.operationId)}
                hasDecodedCalls={!!operationDecodedCalls?.length}
                tenderlyUrl={tenderlyUrl}
                simulationUrl={simulationUrl}
                runTenderlySimulation={runTenderlySimulation}
              />
            }
            footer={
              allowRemove && onRemoveOperation ? (
                <Button
                  variant="secondaryAction"
                  className="text-error"
                  type="button"
                  isDisabled={removingOperationId === operation.operationId}
                  onClick={(e) => {
                    e.stopPropagation()
                    void onRemoveOperation(operation.operationId)
                  }}
                >
                  Remove from batch
                </Button>
              ) : undefined
            }
          />
        ),
        initialExpanded: false,
      }
    })
  }, [
    allowRemove,
    batchOperations,
    chainId,
    decodedCalls,
    failingCallIndexes,
    onRemoveOperation,
    removingOperationId,
    runTenderlySimulation,
    showCallsByOpId,
    simulationUrl,
    smartAccountAddress,
    tenderlyUrl,
    toggleShowCalls,
  ])

  return (
    <List
      label="Transactions"
      items={items}
      emptyListText="No batched transactions"
    />
  )
}
